import { announce } from "@kovernow/flows/announce";
import {
  kycQueryKeys,
  parseStarted,
  serializeStarted,
  SHUFTI_ADDRESS_MAX,
  SHUFTI_POLL_INTERVAL_MS,
  SHUFTI_POLL_WINDOW_MS,
  SHUFTI_RETURN_PARAM,
  SHUFTI_RETURN_VALUE,
  shuftiRecovery,
  validateHkAddress,
  VERIFY_STARTED_KEY,
  type ShuftiStarted,
  type ShuftiVerification,
} from "@kovernow/flows/kyc";
import { profileQueryKeys } from "@kovernow/flows/profile";
import { ApiError, toRecoveryAction, type RecoveryAction } from "@kovernow/flows/recovery";
import { FocusHeading } from "@kovernow/react-ui/a11y/focus-heading";
import { Button } from "@kovernow/react-ui/components/button";
import { InfoBanner } from "@kovernow/react-ui/components/info-banner";
import { Label } from "@kovernow/react-ui/components/label";
import { Textarea } from "@kovernow/react-ui/components/textarea";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Page } from "../../components/Page";
import { Recovery } from "../../components/Recovery";
import { useServices } from "../../core/app-context";
import { useProfile } from "../../core/queries";
import { verifyReturnTo } from "../../core/safe-path";
import { useT } from "../../core/use-t";
import { NeedHelpVerifying } from "./NeedHelpVerifying";
import {
  readStoredReturnTo,
  useBfcacheReset,
  VerifiedBanner,
  VerifyLoading,
  writeStoredReturnTo,
} from "./verify-shared";

function readStarted(): ShuftiStarted | null {
  try {
    return parseStarted(window.sessionStorage.getItem(VERIFY_STARTED_KEY));
  } catch {
    return null;
  }
}

function writeStarted(value: ShuftiStarted | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(VERIFY_STARTED_KEY);
    else window.sessionStorage.setItem(VERIFY_STARTED_KEY, serializeStarted(value));
  } catch {
    // Storage unavailable: the return shows Start.
  }
}

type Mode = "start" | "return";

/**
 * W-8 `/account/verify` (HK): verify with ShuftiPro by full-page redirect and
 * return. Start (address) → the backend creates the verification → ShuftiPro's
 * hosted page (HKID front/back, live selfie, proof of address, camera only) →
 * back here with `?shufti=returned` → poll the server's latest record (3 s, up
 * to 2 min) → `returnTo`. The result comes only from the backend, which only
 * learns it from ShuftiPro's signed callback; the browser never posts one.
 */
export function VerifyIdentityHk() {
  const t = useT();
  const { shuftiApi, leavePage, format } = useServices();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const profile = useProfile();

  // Read once (StrictMode-safe).
  const [isReturn] = useState(() => params.get(SHUFTI_RETURN_PARAM) === SHUFTI_RETURN_VALUE);
  const [started, setStarted] = useState(() => (isReturn ? readStarted() : null));
  const [returnTo, setReturnTo] = useState(() =>
    verifyReturnTo(isReturn ? readStoredReturnTo() : params.get("returnTo")),
  );
  const [mode, setMode] = useState<Mode>(isReturn ? "return" : "start");
  const [late, setLate] = useState(false);
  const [failure, setFailure] = useState<{
    action: RecoveryAction;
    message?: string;
  } | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpDoneRef, setHelpDoneRef] = useState<string | null>(null);

  // Strip `?shufti=returned` once (replace: Back never re-enters the return).
  // Once only: `setParams` changes with the location, and a re-run during the
  // navigation to `returnTo` would pull the customer back here.
  const strippedRef = useRef(false);
  useEffect(() => {
    if (!isReturn || strippedRef.current) return;
    strippedRef.current = true;
    setParams(
      (p) => {
        p.delete(SHUFTI_RETURN_PARAM);
        return p;
      },
      { replace: true },
    );
  }, [isReturn, setParams]);

  const verified = profile.data?.isAuthenticated === true;
  const polling = mode === "return" && !late && !failure;

  const latest = useQuery({
    queryKey: kycQueryKeys.shuftiLatest(),
    queryFn: () => shuftiApi.latest(),
    enabled: profile.isSuccess && !verified,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchInterval: (q) =>
      polling && (q.state.data as ShuftiVerification | null | undefined)?.status === "pending"
        ? SHUFTI_POLL_INTERVAL_MS
        : false,
  });

  // The return polls for up to 2 min, then shows the late-result copy.
  useEffect(() => {
    if (mode !== "return" || late) return;
    const id = setTimeout(() => setLate(true), SHUFTI_POLL_WINDOW_MS);
    return () => clearTimeout(id);
  }, [mode, late]);

  const finishVerified = useCallback(async () => {
    writeStoredReturnTo(null);
    writeStarted(null);
    queryClient.removeQueries({ queryKey: kycQueryKeys.all });
    await queryClient.invalidateQueries({ queryKey: profileQueryKeys.all });
    announce(t("verify.hk.verified"));
    navigate(returnTo, { replace: true });
  }, [navigate, queryClient, returnTo, t]);

  // Back from ShuftiPro with the profile already verified (the signed
  // callback beat the redirect): go straight on to `returnTo`.
  const finishedRef = useRef(false);
  useEffect(() => {
    if (mode !== "return" || !verified || finishedRef.current) return;
    finishedRef.current = true;
    void finishVerified();
  }, [mode, verified, finishVerified]);

  // Act on the server's record while on a return.
  const record = latest.data;
  useEffect(() => {
    if (mode !== "return" || !latest.isSuccess || verified) return;
    if (record === null || record === undefined || !started || record.reference !== started.reference) {
      // Not this tab's verification (or another account): start over, nothing to resume.
      writeStoredReturnTo(null);
      writeStarted(null);
      setReturnTo(verifyReturnTo(null));
      // Never offer a foreign reference to "Need help" afterwards.
      setStarted(null);
      setMode("start");
      return;
    }
    switch (record.status) {
      case "accepted":
        if (!finishedRef.current) {
          finishedRef.current = true;
          void finishVerified();
        }
        break;
      case "declined":
        setFailure({
          action: shuftiRecovery("declined"),
          message: record.declinedReason || undefined,
        });
        break;
      case "cancelled":
      case "timeout":
      case "invalid":
        setFailure({ action: shuftiRecovery(record.status) });
        break;
      default:
        break;
    }
  }, [mode, latest.isSuccess, record, started, verified, finishVerified]);

  useEffect(() => {
    if (verified) {
      // Nothing left to resume.
      writeStoredReturnTo(null);
      writeStarted(null);
    }
  }, [verified]);

  const backToStart = () => {
    setFailure(null);
    setLate(false);
    setMode("start");
  };

  const helpReference = record?.reference ?? started?.reference;
  const help = (showTrigger: boolean) => (
    <NeedHelpVerifying
      open={helpOpen}
      onOpen={() => setHelpOpen(true)}
      onClose={() => setHelpOpen(false)}
      doneRef={helpDoneRef}
      onDone={setHelpDoneRef}
      reference={helpReference}
      showTrigger={showTrigger}
    />
  );
  const helpAction = {
    id: "need_help",
    label: t("verify.hk.help.button"),
    onSelect: () => setHelpOpen(true),
  };

  let body;
  if (profile.isPending) {
    body = <VerifyLoading label={t("common.loading")} />;
  } else if (profile.isError) {
    body = (
      <div data-testid="verify-profile-error">
        <Recovery action={toRecoveryAction(profile.error)} onRetry={() => void profile.refetch()} retryable />
      </div>
    );
  } else if (verified) {
    body = <VerifiedBanner returnTo={returnTo} />;
  } else if (failure) {
    body = (
      <div className="flex flex-col gap-4">
        <div data-testid="verify-failure" data-error={failure.action.errorCode}>
          <Recovery
            action={failure.action}
            message={failure.message}
            onRetry={backToStart}
            handlers={{ retry: backToStart }}
            support={helpAction}
          />
        </div>
        {help(false)}
      </div>
    );
  } else if (mode === "return") {
    if (latest.isPending) {
      body = <VerifyLoading label={t("verify.hk.checkingTitle")} />;
    } else if (latest.isError && latest.data === undefined) {
      body = (
        <div className="flex flex-col gap-4">
          <div data-testid="verify-latest-error">
            <Recovery
              action={toRecoveryAction(latest.error)}
              onRetry={() => void latest.refetch()}
              retryable
              support={helpAction}
            />
          </div>
          {help(false)}
        </div>
      );
    } else {
      body = (
        <Checking
          late={late}
          createdAt={record?.createdAt ?? started?.startedAt}
          format={format}
          onStartAgain={backToStart}
          help={help(true)}
        />
      );
    }
  } else if (latest.isPending) {
    body = <VerifyLoading label={t("common.loading")} />;
  } else {
    body = (
      <StartForm
        pending={latest.data?.status === "pending" ? latest.data : null}
        format={format}
        help={help(true)}
        onCreated={(start) => {
          writeStoredReturnTo(returnTo);
          writeStarted({
            reference: start.reference,
            startedAt: start.createdAt,
          });
          // The page leaves; the button stays busy until it does.
          leavePage(start.verificationUrl);
        }}
        onAlreadyVerified={() => void profile.refetch()}
        onFailure={(action) => setFailure({ action })}
      />
    );
  }

  return (
    <Page titleKey="nav.verifyIdentity" flowKey="nav.account" focusKey={mode === "return" ? null : undefined}>
      {body}
    </Page>
  );
}

function Checking({
  late,
  createdAt,
  format,
  onStartAgain,
  help,
}: {
  late: boolean;
  createdAt?: string;
  format: ReturnType<typeof useServices>["format"];
  onStartAgain: () => void;
  help: ReactNode;
}) {
  const t = useT();
  const time = createdAt ? format.time(createdAt) : "";
  return (
    <section className="flex flex-col gap-4" data-testid={late ? "verify-late" : "verify-checking"}>
      {/* External return (full page load): focus the step heading on mount (EXPERIENCE.md). */}
      <FocusHeading as="h2" focusKey={late ? "late" : "checking"} className="text-xl">
        {late ? t("verify.hk.lateTitle") : t("verify.hk.checkingTitle")}
      </FocusHeading>
      {time ? <p className="text-sm text-muted-foreground">{t("verify.hk.checkingStarted", { time })}</p> : null}
      {/* One persistent live region for the status text only, not the buttons or help panel. */}
      <p aria-live="polite">{late ? t("verify.hk.lateBody") : t("verify.hk.checkingBody")}</p>
      {late ? (
        <div>
          <Button asChild variant="default" size="touch">
            <Link to="/vault" data-testid="verify-late-vault">
              {t("verify.hk.lateVault")}
            </Link>
          </Button>
        </div>
      ) : null}
      <div>
        <Button type="button" variant="outline" size="touch" onClick={onStartAgain} data-testid="verify-start-again">
          {t("verify.hk.startAgain")}
        </Button>
      </div>
      {help}
    </section>
  );
}

function StartForm({
  pending,
  format,
  help,
  onCreated,
  onAlreadyVerified,
  onFailure,
}: {
  pending: ShuftiVerification | null;
  format: ReturnType<typeof useServices>["format"];
  help: ReactNode;
  onCreated: (start: { reference: string; verificationUrl: string; createdAt: string }) => void;
  onAlreadyVerified: () => void;
  onFailure: (action: RecoveryAction) => void;
}) {
  const t = useT();
  const id = useId();
  const { shuftiApi } = useServices();
  const [address, setAddress] = useState("");
  const [touched, setTouched] = useState(false);
  const [starting, setStarting] = useState(false);
  const startingRef = useRef(false);

  const unstick = useCallback(() => {
    startingRef.current = false;
    setStarting(false);
  }, []);
  useBfcacheReset(unstick);

  const addressErrorKey = touched ? validateHkAddress(address) : undefined;
  const addressId = `${id}-address`;
  const hintId = `${id}-address-hint`;
  const errorId = `${id}-address-error`;

  const start = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (validateHkAddress(address) || startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    try {
      const created = await shuftiApi.createVerification(address);
      onCreated(created);
    } catch (err) {
      startingRef.current = false;
      setStarting(false);
      if (err instanceof ApiError && err.status === 409) {
        onAlreadyVerified();
        return;
      }
      onFailure(shuftiRecovery(err));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <form noValidate onSubmit={(e) => void start(e)} className="flex flex-col gap-6" data-testid="verify-start">
        {pending ? (
          <InfoBanner
            tone="info"
            title={t("verify.hk.inProgress", {
              time: format.time(pending.createdAt),
            })}
            body={t("verify.hk.inProgressBody")}
            data-testid="verify-in-progress"
          />
        ) : null}
        <div className="flex flex-col gap-2">
          <p>{t("verify.hk.intro")}</p>
          <ul className="list-disc pl-6">
            <li>{t("verify.hk.needHkid")}</li>
            <li>{t("verify.hk.needSelfie")}</li>
            <li>{t("verify.hk.needAddress")}</li>
          </ul>
          <p className="text-sm text-muted-foreground">{t("verify.hk.cameraNote")}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={addressId}>{t("verify.hk.addressLabel")}</Label>
          <p id={hintId} className="text-sm text-muted-foreground">
            {t("verify.hk.addressHint")}
          </p>
          <Textarea
            id={addressId}
            value={address}
            rows={3}
            maxLength={SHUFTI_ADDRESS_MAX}
            autoComplete="street-address"
            required
            aria-required
            aria-invalid={addressErrorKey ? true : undefined}
            aria-describedby={[hintId, addressErrorKey ? errorId : null].filter(Boolean).join(" ")}
            onChange={(e) => setAddress(e.target.value)}
            onBlur={() => setTouched(true)}
            className="min-h-20"
            data-testid="verify-address"
          />
          {addressErrorKey ? (
            <p id={errorId} className="text-sm font-semibold text-status-error">
              {t(addressErrorKey)}
            </p>
          ) : null}
        </div>
        <div>
          <Button
            type="submit"
            size="touch"
            variant="cta"
            aria-busy={starting || undefined}
            aria-disabled={starting || undefined}
            data-testid="verify-start-button"
          >
            {starting ? t("verify.hk.starting") : pending ? t("verify.hk.startAgain") : t("verify.hk.start")}
          </Button>
        </div>
      </form>
      {help}
    </div>
  );
}
